import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractComponent } from '@app-shared/components/form/abstract/abstract.component';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

type TypeOptions = 'number' | 'text' | 'email' | 'password' | 'radio';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [CommonModule, TranslatePipe, FormsModule],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent extends AbstractComponent implements OnInit, AfterViewInit {
  @Input() type!: TypeOptions;
  @Input() name?: string;
  @Input() placeholder?: string;
  @Input() size?: 'small' | 'medium' = 'medium';
  @Input() suggestedValue?: number | string;
  @Input() suggestedValueLabel?: string;
  @Input() infoLabel?: string;
  @Input() testId?: string;
  @Input() autocapitalize?: string; // affects behaviour only when typing on virtual keyboard
  @Input() forceAutofocus?: boolean; // e.g. for 2FA
  @Input() onInputCallback?: (arg0: any) => any;
  @Input() hasSuccess = false;
  @Input() hasError = false;

  @Input() errorMessage?: string | undefined;
  @Input() isValid?: boolean;

  @Output() blurEvent = new EventEmitter<void>();

  @ViewChild('input') inputElement!: ElementRef;

  isFocused = false;

  constructor() {
    super();
  }

  ngOnInit() {
    setTimeout(() => {
      // this will make the execution after the above boolean has changed
      if (this.forceAutofocus) {
        this.inputElement.nativeElement.focus();
      }
    }, 0);
  }

  ngAfterViewInit() {
    // Check for autofilled fields after view initialization
    setTimeout(() => {
      this.detectAutofill();
    }, 500);
  }

  private detectAutofill(): void {
    if (!this.inputElement) return;

    const input = this.inputElement.nativeElement;

    // If the input has a value but our model doesn't, it's likely an autofill
    if (input.value && !super.value) {
      // Update the model value
      const autofillValue = this.getTypedValue(input.value);
      super.value = autofillValue;
      this.onChange(autofillValue);
      this.onTouched();
    }
  }

  get value() {
    return this.getTypedValue(super.value);
  }

  set value(value) {
    const typedValue = this.getTypedValue(value);
    super.value = typedValue;
    this.onChange(typedValue);
    this.onTouched();
  }

  getTypedValue(value: string | number): string | number {
    return this.type === 'number' && String(value).length > 0 ? Number(value) : value;
  }

  onInput() {
    if (this.onInputCallback) {
      this.onInputCallback(this.inputElement.nativeElement);
    }
  }

  onBlur() {
    this.isFocused = false;
    this.onTouched();
    this.blurEvent.emit();

    // Additional check on blur to catch any autofill that happened
    this.detectAutofill();
  }

  get inputId(): string {
    return `${this.type}-${this.id}`;
  }

  get inputGroupClasses(): { [key: string]: boolean } {
    return {
      'cm-input-group-small': this.size === 'small',
      'cm-input-group-has-error': this.hasError || this.showError,
      'cm-input-group-has-success': this.hasSuccess,
    };
  }
}
